import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import type { Json } from '../types/database.types'

export interface UserSettingsAggregate {
  showRecoveryIndicator: boolean
  weekStartsMonday: boolean
  showDisciplineColors: boolean
  showWeatherIndicator: boolean
  showSleepIndicator: boolean
  showCalendarSummary: boolean
}

const DEFAULT_SETTINGS: UserSettingsAggregate = {
  showRecoveryIndicator: true,
  weekStartsMonday: true,
  showDisciplineColors: true,
  showWeatherIndicator: true,
  showSleepIndicator: true,
  showCalendarSummary: false,
}

const SETTINGS_KEY = 'calendar_settings'

export const useUserSettings = () => {
  const { user } = useAuth()
  const [settings, setSettings] = useState<UserSettingsAggregate>(DEFAULT_SETTINGS)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load settings from database on mount
  const loadSettings = useCallback(async () => {
    if (!user) {
      setSettings(DEFAULT_SETTINGS)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const { data, error: dbError } = await supabase
        .from('user_settings')
        .select('setting_value')
        .eq('user_id', user.id)
        .eq('setting_key', SETTINGS_KEY)
        .single()

      if (dbError && dbError.code !== 'PGRST116') {
        // PGRST116 is "no rows returned"
        throw dbError
      }

      if (data?.setting_value) {
        const savedSettings = data.setting_value as Partial<UserSettingsAggregate>
        setSettings((prevSettings) => ({
          ...prevSettings,
          ...savedSettings,
        }))
      }
    } catch (err) {
      console.error('Failed to load user settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to load settings')
    } finally {
      setIsLoading(false)
    }
  }, [user])

  // Save settings to database
  const saveSettings = useCallback(
    async (newSettings: UserSettingsAggregate) => {
      if (!user) return

      try {
        const { error: dbError } = await supabase.from('user_settings').upsert(
          {
            user_id: user.id,
            setting_key: SETTINGS_KEY,
            setting_value: newSettings as unknown as Json,
          },
          {
            onConflict: 'user_id,setting_key',
          }
        )

        if (dbError) {
          throw dbError
        }
      } catch (err) {
        console.error('Failed to save user settings:', err)
        setError(err instanceof Error ? err.message : 'Failed to save settings')
        throw err // Re-throw to allow caller to handle
      }
    },
    [user]
  )

  // Load settings when user changes
  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const updateSetting = useCallback(
    async (key: keyof UserSettingsAggregate, value: boolean) => {
      const newSettings = {
        ...settings,
        [key]: value,
      }

      // Optimistically update UI
      setSettings(newSettings)

      try {
        await saveSettings(newSettings)
      } catch (err) {
        // Revert on error
        setSettings(settings)
      }
    },
    [settings, saveSettings]
  )

  const resetSettings = useCallback(async () => {
    // Optimistically update UI
    setSettings(DEFAULT_SETTINGS)

    try {
      await saveSettings(DEFAULT_SETTINGS)
    } catch (err) {
      // Revert on error
      setSettings(settings)
    }
  }, [settings, saveSettings])

  return {
    settings,
    isLoading,
    error,
    updateSetting,
    resetSettings,
    loadSettings,
  }
}
